/**
 * Types for table data responses in chat messages.
 *
 * Enables the backend to return tabular data that is rendered
 * as a styled, interactive table inline in the chat.
 *
 * Convention: When a message contains table data, it uses the format:
 * <!-- TABLE_DATA:{ ... JSON ... } -->
 * Followed by the regular text content (summary).
 */

export interface TableColumn {
  key: string
  label: string
  align?: 'left' | 'center' | 'right'
  format?: 'currency_brl' | 'date_br' | 'number'
}

export interface TableSummary {
  total: number
  avg_days?: number
  max_days?: number
  oldest_os?: number | null
  total_value?: number
  // Contadores específicos por relatório (ex.: Pedidos CSPS expõe
  // `atribuido_count`, `backorder_count`, etc). Index signature
  // evita ter que estender este tipo para cada novo relatório.
  [key: string]: number | null | undefined
}

export interface TableData {
  type: 'table'
  title: string
  subtitle?: string
  columns: TableColumn[]
  rows: Record<string, unknown>[]
  summary?: TableSummary
  exportable?: boolean
  emailable?: boolean
  /**
   * Chave de template — usada tanto pelo e-mail (`emailTemplates.ts`,
   * `TEMPLATES[email_template]`) quanto pelo texto do WhatsApp
   * (`WhatsAppTextModal.tsx`, `WHATSAPP_BUILDERS[email_template]`). O nome
   * do campo é histórico (só e-mail originalmente); sem builder registrado
   * em algum dos dois, cai no template/builder padrão.
   */
  email_template?: string
  whatsappable?: boolean
  /** Colunas expandidas para export Excel (quando diferente da tela) */
  export_columns?: TableColumn[]
  /**
   * Registro bruto que alguns atalhos anexam para auditoria (ex.: Conferência de
   * Peça: cadeia lida no portal e estado da consulta ao SGE). Não vira coluna.
   */
  auditoria?: Record<string, unknown>
  /**
   * Nome da chave em cada row que contém uma cor hex (ex: "#BBF7D0") para
   * pintar a linha na renderização e no export XLSX. Quando ausente, a
   * tabela não é colorida por linha.
   *
   * Convenção atual: backend usa `_row_color`. Ver
   * `apps/agents/tools/list_open_pedidos.py` no backend.
   */
  row_color_field?: string
  /**
   * Lista de chaves de colunas que devem ser tintadas (default: linha inteira).
   * Ex: `["status"]` tinta APENAS a célula da coluna Status, deixando o
   * resto da linha sem fill. Reproduz o padrão da Maira (transcript
   * 06:00–06:35) — ela só pinta a célula de status manualmente.
   */
  row_color_columns?: string[]
  /**
   * Quando presente, o front renderiza abas (tabs) acima da tabela para
   * alternar entre a tabela principal (atual) e a secundária. Usado em
   * `list_open_helpdesks` com `include_csps=true`: a tab principal é
   * "HDs em Aberto" e a secundária é "Pedidos CSPS" (lista completa do
   * período, não só os vinculados). Export XLSX e WhatsApp respeitam a
   * tab ativa.
   */
  secondary_tab?: {
    tab_label: string
    primary_tab_label: string
    table: TableData
  }
}

/**
 * Regular expression to detect table data in message content
 */
export const TABLE_DATA_REGEX = /<!--\s*TABLE_DATA:([\s\S]*?)-->/

/**
 * Parse table data from message content
 */
export function parseTableData(content: string): {
  data: TableData
  textContent: string
} | null {
  const match = content.match(TABLE_DATA_REGEX)

  if (!match) {
    return null
  }

  try {
    const jsonStr = match[1].trim()
    const data = JSON.parse(jsonStr) as TableData

    if (
      data.type !== 'table' ||
      !Array.isArray(data.columns) ||
      !Array.isArray(data.rows)
    ) {
      return null
    }

    const textContent = content.replace(TABLE_DATA_REGEX, '').trim()

    return { data, textContent }
  } catch {
    return null
  }
}

/**
 * Check if a message contains table data
 */
export function hasTableData(content: string): boolean {
  return TABLE_DATA_REGEX.test(content)
}
